import * as THREE from "three";
import type { InteriorPanelDirection } from "../../domain/finishes/types";
import { heaterKindOf } from "../../domain/geometry/buildingDimensions";
import { BENCH_SLAT_THICKNESS_MM } from "../../domain/interior/constants";
import { roofDeckingVerticalThicknessMm } from "../../domain/roof/roofDecking";
import {
	createCeilingVisibilityGroup,
	getCeilingVisibilityGroup,
} from "../ceilingVisibility";
import {
	ENGINE_INTERIOR_BATTEN_CC_M,
	ENGINE_INTERIOR_BATTEN_D_M,
	ENGINE_INTERIOR_BATTEN_W_M,
	ENGINE_INTERIOR_LINING_D_M,
	ENGINE_VAPOR_BARRIER_T_M,
} from "../constants";
import { buildChimneyStack, CHIMNEY_OUTER_RADIUS_M } from "../chimneyGeometry";
import { addSlattedBench, addWallVent } from "../detailHelpers";
import { addBox, addSlopedBox } from "../geometry";
import { buildHeaterGroup, getHeaterWidth } from "../heaterGeometry";
import { benchSeatCentreYM } from "../interiorRenderPlan";
import {
	getBenchMaterial,
	matCeilingPanel,
	matChimney,
	matInsulation,
	matRoofFlashing,
	matShield,
	matVaporBarrier,
	matWood,
} from "../materials";
import {
	renderInsulationBays,
	renderInteriorLyingBattenStrips,
	renderInteriorStandingBattenStrips,
	renderVaporBarrierStrips,
} from "../wallStrips";
import { memberPiecesAround } from "../../domain/chimney/chimneyTrimmer";
import {
	getCeilingBattenRows,
	getCeilingStructuralExtents,
	planCeilingBattenTrim,
	TRIMMED_PIECE_SUFFIX,
} from "./chimneyTrim";
import type {
	FlueAxisM,
	SaunaCoreBuildOptions,
	SquareBaseBuildContext,
	SquareBaseWall,
	WallFramingResult,
} from "./context";
import {
	getRoofCoveringThickness,
	getRoofJoistUndersideYAt,
	getRoofJoistZPositions,
	ROOF_UNDERLAY_T,
} from "./roof";

const INTERIOR_CEILING_PANEL_T = 0.015;
const INTERIOR_CEILING_BATTEN_D = ENGINE_INTERIOR_BATTEN_D_M;
const INTERIOR_BATTEN_W = ENGINE_INTERIOR_BATTEN_W_M;
const CHIMNEY_PLATE_SURFACE_GAP = 0.004;

/**
 * Depth of the ceiling lining hung UNDER the structural ceiling plane:
 * bastufolie stapled to the joist undersides, then the takgles it holds up.
 * The bastupanel's top face sits at the bottom of this stack.
 *
 * This is the SAME stack the walls carry (`ctx.iL` / `ENGINE_INTERIOR_LINING_D_M`,
 * issue 114) — folie + läkt — so it reads the shared constant rather than
 * re-adding the same two terms. Before
 * issue 115 the ceiling had no such offset: the battens were drawn ABOVE the
 * panel, i.e. up inside the roof-joist volume, which is the same buried-layer
 * bug the walls had. Every ceiling Y now derives from ONE reference plane
 * (`getCeilingStructureYAt`) minus a cumulative offset, so the layer
 * order cannot silently disagree with itself.
 */
const CEILING_VAPOR_BARRIER_T = ENGINE_VAPOR_BARRIER_T_M;
const CEILING_LINING_D = ENGINE_INTERIOR_LINING_D_M;

/**
 * Taklist / bomlist — the BOM row `interior.ceiling-trim`. Same 22×45 stock
 * as the takgles, so it tracks the same constants rather than re-literalising
 * the dimension a third time in this file.
 */
const CEILING_TRIM_T = ENGINE_INTERIOR_BATTEN_D_M;
const CEILING_TRIM_H = ENGINE_INTERIOR_BATTEN_W_M;

/**
 * Keeps adjacent layers from sharing a rendered face. Coplanar contact
 * faces z-fight in exactly the view the layer exists for (issue 114).
 */
const FACE_CLEARANCE_M = 0.001;

function getInteriorCeilingLength(ctx: SquareBaseBuildContext) {
	return Math.max(0.2, ctx.L - 2 * ctx.sD - 2 * ctx.iL - 2 * ctx.pT);
}

function getInteriorCeilingHalfWidth(ctx: SquareBaseBuildContext) {
	return Math.max(0.12, ctx.W / 2 - ctx.sD - ctx.iL - ctx.pT);
}

/**
 * Y of the STRUCTURAL ceiling plane — the roof joists' underside, which is
 * what the ceiling lining is actually fastened to. Every ceiling layer is
 * this plane minus a cumulative offset; nothing else may re-derive it.
 *
 * It delegates to `roof.ts`, which owns where the joists are. This used to
 * anchor its own fall at the FINISHED ceiling edge rather than the wall
 * line, which slid the whole stack `(sD + iL + pT)·tan(pitch)` below the
 * joists — so the "bay" insulation hung under them with a matching void
 * above it. The plane is unbounded on purpose: the takgles, folie and batts
 * all run past the panelled edge out to the hammarband.
 */
function getCeilingStructureYAt(ctx: SquareBaseBuildContext, x = 0, z = 0) {
	return getRoofJoistUndersideYAt(ctx, x, z);
}

/**
 * The same plane CLAMPED to the finished (panelled) ceiling. Clearance
 * consumers — heater headroom, the chimney flange — ask about points inside
 * the room, where a query drifting outside the panelled extent should read
 * the edge height rather than an extrapolated one.
 */
function getFinishedCeilingStructureYAt(
	ctx: SquareBaseBuildContext,
	x = 0,
	z = 0,
) {
	if (ctx.roofConfig.kind === "gable") {
		const innerHalfWidth = getInteriorCeilingHalfWidth(ctx);
		return getCeilingStructureYAt(
			ctx,
			Math.min(innerHalfWidth, Math.abs(x)),
			z,
		);
	}

	if (ctx.roofGeometry.monoSlope?.axis === "x") {
		// Falls across the width: clamp the query to the panelled X extent.
		const innerHalfWidth = getInteriorCeilingHalfWidth(ctx);
		return getCeilingStructureYAt(
			ctx,
			Math.max(-innerHalfWidth, Math.min(innerHalfWidth, x)),
			z,
		);
	}

	const ceilingHalfLength = getInteriorCeilingLength(ctx) / 2;
	return getCeilingStructureYAt(
		ctx,
		x,
		Math.max(-ceilingHalfLength, Math.min(ceilingHalfLength, z)),
	);
}

/** Top face of the bastupanel — the structural plane less folie + takgles. */
function getInteriorCeilingPanelTopYAt(
	ctx: SquareBaseBuildContext,
	x = 0,
	z = 0,
) {
	return getFinishedCeilingStructureYAt(ctx, x, z) - CEILING_LINING_D;
}

function getInteriorCeilingPanelCenterYAt(
	ctx: SquareBaseBuildContext,
	x = 0,
	z = 0,
) {
	return (
		getInteriorCeilingPanelTopYAt(ctx, x, z) - INTERIOR_CEILING_PANEL_T / 2
	);
}

/**
 * Y of the interior ceiling panel underside at a point in the room —
 * the surface that caps vertical clearance checks (e.g. heater safety
 * clearance). Follows the sloped ceiling for mono/gable roofs.
 */
export function getInteriorCeilingUndersideYAt(
	ctx: SquareBaseBuildContext,
	x = 0,
	z = 0,
) {
	return getInteriorCeilingPanelTopYAt(ctx, x, z) - INTERIOR_CEILING_PANEL_T;
}

/** Centre of the takgles, immediately above the panel's top face. */
function getInteriorCeilingBattenCenterYAt(
	ctx: SquareBaseBuildContext,
	x = 0,
	z = 0,
) {
	return (
		getCeilingStructureYAt(ctx, x, z) -
		CEILING_LINING_D +
		INTERIOR_CEILING_BATTEN_D / 2
	);
}

/** Centre of the bastufolie, immediately under the structural plane. */
function getInteriorCeilingVaporCenterYAt(
	ctx: SquareBaseBuildContext,
	x = 0,
	z = 0,
) {
	return getCeilingStructureYAt(ctx, x, z) - CEILING_VAPOR_BARRIER_T / 2;
}

function getChimneyPlateSlope(ctx: SquareBaseBuildContext, x: number) {
	if (ctx.roofConfig.kind === "gable") {
		return {
			pitchRad: x <= 0 ? ctx.pitchRad : -ctx.pitchRad,
			axis: "x" as const,
		};
	}

	return getMonoCeilingShear(ctx);
}

/**
 * The shear a mono ceiling/roof layer takes in `addSlopedBox`: `pitch` along
 * +Z for the historical front-high roof, `sign·pitch` along the run axis for
 * any other high wall (a left/right high wall falls across the width).
 */
function getMonoCeilingShear(ctx: SquareBaseBuildContext): {
	pitchRad: number;
	axis: "x" | "z";
} {
	const slope = ctx.roofGeometry.monoSlope;
	if (slope && slope.highWall !== "front") {
		return { pitchRad: slope.signedPitchRad, axis: slope.axis };
	}
	return {
		pitchRad: ctx.pitchRad,
		axis: "z" as const,
	};
}

function createSlopedStormCollarGeometry(
	topRadius: number,
	bottomRadius: number,
	height: number,
	radialSegments: number,
	slope: ReturnType<typeof getChimneyPlateSlope>,
) {
	const geometry = new THREE.CylinderGeometry(
		topRadius,
		bottomRadius,
		height,
		radialSegments,
	);
	const positions = geometry.attributes.position;
	for (let index = 0; index < positions.count; index += 1) {
		const run =
			slope.axis === "x" ? positions.getX(index) : positions.getZ(index);
		positions.setY(
			index,
			positions.getY(index) + run * Math.tan(slope.pitchRad),
		);
	}
	geometry.computeVertexNormals();
	return geometry;
}

/**
 * Span of a wall (metres from its start) that carries bastufolie, given the
 * hot room's Z extent. Front/back walls are wholly in or out of the hot room;
 * the long walls are clipped to the band. `undefined` means the wall gets no
 * vapour barrier at all.
 */
function resolveVaporBarrierSpan(
	ctx: SquareBaseBuildContext,
	wall: SquareBaseWall,
	zRange?: { min: number; max: number },
): { start: number; end: number } | undefined {
	if (!zRange) return { start: -Infinity, end: Infinity };

	if (wall === "front" || wall === "back") {
		const wallZ = wall === "front" ? ctx.L / 2 : -ctx.L / 2;
		const inside = wallZ >= zRange.min - 0.01 && wallZ <= zRange.max + 0.01;
		return inside ? { start: -Infinity, end: Infinity } : undefined;
	}

	// Left/right run along Z; local span is measured from -L/2.
	const start = zRange.min + ctx.L / 2;
	const end = zRange.max + ctx.L / 2;
	return end - start > 0.02 ? { start, end } : undefined;
}

/**
 * The interior wall layer stack, in the order the build guide locks:
 * regel → stenullsisolering (in the bays) → åldersbeständig bastufolie →
 * LIGGANDE glespanel 22×45 → stående bastupanel (drawn separately by
 * `buildPanels`, which reads the same `ctx.iL` build-out).
 *
 * All three layers live in the interior group so the visibility contract
 * mirrors the facade's: hiding the panel layer reveals the gles, the folie and
 * the insulation behind it; hiding the interior layer strips back to framing.
 *
 * `vaporBarrierZRange` limits the bastufolie to a Z band (world coords). The
 * vapour barrier belongs in the HOT room only — which is also exactly what the
 * BOM buys — so the two-room engine passes its sauna extent and the relax end
 * wall gets none. Omitted (square engine) means the whole building is the hot
 * room and every wall is foiled. Insulation and glespanel are NOT clipped:
 * every wall is insulated and every wall is panelled, per the BOM.
 */
function buildInteriorWallLayers(
	ctx: SquareBaseBuildContext,
	framingResults: Map<SquareBaseWall, WallFramingResult>,
	interiorGroup: THREE.Group,
	frontInteriorGroup: THREE.Group,
	namePrefix: string,
	vaporBarrierZRange?: { min: number; max: number },
	panelDirection: InteriorPanelDirection = "standing",
) {
	for (const wall of ["left", "right", "back", "front"] as const) {
		const result = framingResults.get(wall);
		if (!result) continue;

		const group = wall === "front" ? frontInteriorGroup : interiorGroup;
		const wallH = ctx.roofGeometry.getMaxWallHeight(wall);
		const base = {
			wall,
			W: ctx.W,
			L: ctx.L,
			wallBase: ctx.wallBase,
			roofGeometry: ctx.roofGeometry,
			offset: 0,
			group,
			renderGlass: false,
		} as const;

		renderInsulationBays(
			{ ...base, material: matInsulation },
			result.studAxisPositions,
			result.surfaceOpenings,
			ctx.sW,
			matInsulation,
			`detail/insulation/${namePrefix}-wall`,
			result.horizontalMembers,
		);

		const foilSpan = resolveVaporBarrierSpan(ctx, wall, vaporBarrierZRange);
		if (foilSpan) {
			renderVaporBarrierStrips(
				{
					...base,
					material: matVaporBarrier,
					innerOffset: 0,
					innerAxisRange: foilSpan,
				},
				result.surfaceOpenings,
				wallH,
				ENGINE_VAPOR_BARRIER_T_M,
				`detail/vapor-barrier/${namePrefix}-wall`,
			);
		}

		// Korsläkt: the battens must cross the boards they carry. Drawing lying
		// rows under a lying panel would render a wall the BOM never bought —
		// and the section view would show battens parallel to the panel.
		const battenCtx = {
			...base,
			material: matWood,
			innerOffset: ENGINE_VAPOR_BARRIER_T_M,
		};
		if (panelDirection === "lying") {
			renderInteriorStandingBattenStrips(
				battenCtx,
				result.studAxisPositions,
				result.surfaceOpenings,
				INTERIOR_BATTEN_W,
				INTERIOR_CEILING_BATTEN_D,
				matWood,
				{ namePrefix: `detail/interior-batten/${namePrefix}-wall` },
			);
		} else {
			renderInteriorLyingBattenStrips(
				battenCtx,
				result.surfaceOpenings,
				INTERIOR_BATTEN_W,
				INTERIOR_CEILING_BATTEN_D,
				matWood,
				{
					namePrefix: `detail/interior-batten/${namePrefix}-wall`,
					rowSpacing: ENGINE_INTERIOR_BATTEN_CC_M,
				},
			);
		}
	}
}

/**
 * The inner-ceiling layer stack ABOVE the bastupanel: stenullsisolering in
 * the roof-joist bays, bastufolie under the joists, then the takgles.
 *
 * These live in the INTERIOR group, not the panel group, so the ceiling
 * obeys the same reveal contract as the walls (issue 114): hiding the panel
 * layer exposes the gles, the folie and the batts behind it.
 *
 * They go into the interior group's CEILING VISIBILITY subgroup, though, not
 * straight onto it: this stack is also the underside of the roof, so TAK off
 * has to open the view down into the room instead of leaving a full-coverage
 * batt slab floating over it. The subgroup carries `roof` and its parent
 * carries `interior`, which is exactly the `roof && interior` semantics we
 * want — see `../ceilingVisibility`.
 *
 * The takgles runs along Z — ACROSS the roof joists, which span the width
 * and are spread along the length for every roof kind. Before issue 115 it
 * ran along X, parallel to the joists it was supposed to be nailed to, at a
 * different spacing so it did not even coincide with them.
 */
export function buildInteriorCeilingLayers(
	ctx: SquareBaseBuildContext,
	interiorGroup: THREE.Group,
	namePrefix: string,
	/** A wood stove's flue: the takgles is trimmed around it (100 mm). */
	flue: FlueAxisM | null = null,
) {
	const ceilingGroup = createCeilingVisibilityGroup(interiorGroup);
	buildCeilingInsulation(ctx, ceilingGroup, namePrefix);
	buildCeilingVaporBarrier(ctx, ceilingGroup, namePrefix);
	buildCeilingBattens(ctx, ceilingGroup, namePrefix, flue);
}

/**
 * Shear for a takgles piece centred at x. Gable: each half slopes along X, so
 * a run at a fixed x is LEVEL along its whole run, but its 45 mm width still
 * crosses the sloped plane — at 22° the ceiling drops 18 mm across it — so it
 * is sheared across the width with the half's own sign; the ridge run (x = 0)
 * straddles the apex, where `Math.sign(0) === 0` leaves it flat. Mono/flat:
 * the ceiling falls along one axis and every piece takes the roof's shear.
 */
function ceilingBattenShear(ctx: SquareBaseBuildContext, x: number) {
	if (ctx.roofConfig.kind === "gable") {
		return { pitchRad: -Math.sign(x) * ctx.pitchRad, axis: "x" as const };
	}
	return getMonoCeilingShear(ctx);
}

function buildCeilingBattens(
	ctx: SquareBaseBuildContext,
	group: THREE.Group,
	namePrefix: string,
	flue: FlueAxisM | null,
) {
	const { length } = getCeilingStructuralExtents(ctx);
	const trim = planCeilingBattenTrim(ctx, flue);

	// `addSlopedBox` shears in Y without rotating, so the depth passed in is
	// the horizontal run (2026-07-16 note); a piece is anchored at its own
	// centre, so its Y is asked for there.
	getCeilingBattenRows(ctx, namePrefix).forEach(({ x, name }, index) => {
		const shear = ceilingBattenShear(ctx, x);
		memberPiecesAround(
			-length / 2,
			length / 2,
			trim?.cutIndices.includes(index) ? trim.gapAlongM : null,
		).forEach(([a, b], piece) => {
			const z = (a + b) / 2;
			addSlopedBox(
				INTERIOR_BATTEN_W,
				INTERIOR_CEILING_BATTEN_D,
				b - a,
				matWood,
				x,
				getInteriorCeilingBattenCenterYAt(ctx, x, z),
				z,
				group,
				shear.pitchRad,
				shear.axis,
			).name = piece === 0 ? name : `${name}${TRIMMED_PIECE_SUFFIX}`;
		});
	});

	// The noggins run across X at the headers' z. On a gable one that crosses
	// the ridge is two pieces, one per roof half, like the ceiling itself.
	trim?.headers.forEach((header, index) => {
		const ridge =
			ctx.roofConfig.kind === "gable" &&
			header.fromAcrossM < 0 &&
			header.toAcrossM > 0;
		const spans: [number, number][] = ridge
			? [
					[header.fromAcrossM, 0],
					[0, header.toAcrossM],
				]
			: [[header.fromAcrossM, header.toAcrossM]];
		spans.forEach(([a, b], piece) => {
			const x = (a + b) / 2;
			const shear = ceilingBattenShear(ctx, x);
			addSlopedBox(
				b - a,
				INTERIOR_CEILING_BATTEN_D,
				INTERIOR_BATTEN_W,
				matWood,
				x,
				getInteriorCeilingBattenCenterYAt(ctx, x, header.alongM),
				header.alongM,
				group,
				shear.pitchRad,
				shear.axis,
			).name =
				`detail/interior-batten/${namePrefix}-ceiling-chimney-noggin-${index + 1}${piece === 0 ? "" : "-b"}`;
		});
	});
}

/**
 * Emit one sheared slab per roof PLANE: two mirrored halves falling away from
 * the ridge on a gable, a single full-width slab on mono/flat.
 *
 * The folie, the insulation bays and the ceiling panel are all "a sheet
 * covering the ceiling plane" and each had its own copy of this branch —
 * including the sign convention for which half gets `-pitchRad`. One copy
 * means a change to how a roof half is split cannot land on two of the three
 * layers and miss the other.
 *
 * `centerYAt` is asked for the Y at each slab's own centre X, since the
 * sheared box is anchored about its local origin.
 */
function addCeilingPlaneSlabs(
	ctx: SquareBaseBuildContext,
	group: THREE.Group,
	options: {
		halfWidth: number;
		thickness: number;
		length: number;
		material: THREE.Material;
		z?: number;
		centerYAt: (centerX: number) => number;
		name: (half: "left" | "right" | "top") => string;
	},
) {
	const { halfWidth, thickness, length, material, z = 0 } = options;

	if (ctx.roofConfig.kind === "gable") {
		for (const side of [-1, 1] as const) {
			const centerX = (side * halfWidth) / 2;
			addSlopedBox(
				halfWidth,
				thickness,
				length,
				material,
				centerX,
				options.centerYAt(centerX),
				z,
				group,
				-side * ctx.pitchRad,
				"x",
			).name = options.name(side < 0 ? "left" : "right");
		}
		return;
	}

	const monoShear = getMonoCeilingShear(ctx);
	addSlopedBox(
		2 * halfWidth,
		thickness,
		length,
		material,
		0,
		options.centerYAt(0),
		z,
		group,
		monoShear.pitchRad,
		monoShear.axis,
	).name = options.name("top");
}

function buildCeilingVaporBarrier(
	ctx: SquareBaseBuildContext,
	group: THREE.Group,
	namePrefix: string,
) {
	const { halfWidth, length } = getCeilingStructuralExtents(ctx);
	const prefix = `detail/vapor-barrier/${namePrefix}-ceiling`;

	addCeilingPlaneSlabs(ctx, group, {
		halfWidth,
		thickness: CEILING_VAPOR_BARRIER_T,
		length,
		material: matVaporBarrier,
		centerYAt: (centerX) => getInteriorCeilingVaporCenterYAt(ctx, centerX, 0),
		name: (half) => `${prefix}-${half}`,
	});
}

/**
 * Stenullsisolering in the roof-joist bays. Bays are the gaps between the
 * joist Z centres the roof builder actually uses, so adding or resizing
 * joists moves the batts with them instead of drifting.
 */
function buildCeilingInsulation(
	ctx: SquareBaseBuildContext,
	group: THREE.Group,
	namePrefix: string,
) {
	const { halfWidth } = getCeilingStructuralExtents(ctx);
	const joistZ = getRoofJoistZPositions(ctx);
	const battH = Math.max(0.02, ctx.rH - 2 * FACE_CLEARANCE_M);
	const prefix = `detail/insulation/${namePrefix}-ceiling`;

	for (let index = 0; index < joistZ.length - 1; index += 1) {
		const bayDepth = joistZ[index + 1] - joistZ[index] - ctx.fW;
		if (bayDepth <= 0.02) continue;
		const centerZ = (joistZ[index] + joistZ[index + 1]) / 2;

		addCeilingPlaneSlabs(ctx, group, {
			halfWidth,
			thickness: battH,
			length: bayDepth,
			material: matInsulation,
			z: centerZ,
			centerYAt: (centerX) =>
				getCeilingStructureYAt(ctx, centerX, centerZ) +
				FACE_CLEARANCE_M +
				battH / 2,
			// Mono has one bay-wide batt per bay, so it needs no half suffix.
			name: (half) =>
				half === "top"
					? `${prefix}-${index + 1}`
					: `${prefix}-${half}-${index + 1}`,
		});
	}
}

/**
 * Bastupanel (innertak) + taklist/bomlist — the two layers that stay in the
 * PANEL group, so hiding the panel strips the ceiling back to its gles.
 *
 * The panel boards run along X, crossing the Z-running takgles they are
 * nailed to. `matCeilingPanel` is the 90°-rotated texture variant that makes
 * the rendered boards agree with that; with the default panel material the
 * planks read along Z, parallel to the battens.
 */
export function buildInteriorCeilingFinish(
	ctx: SquareBaseBuildContext,
	ceilingGroup: THREE.Group,
	namePrefix: string,
	/**
	 * Front-wall visibility group, same contract as `buildPanels`: the
	 * front-wall taklist is a front-wall detail, so hiding the front wall for
	 * a cutaway must take it with it instead of leaving it floating across
	 * the opening. REQUIRED — as an optional param with a fallback this fix
	 * compiled green while every caller kept passing nothing, i.e. did
	 * nothing at all.
	 */
	frontCeilingGroup: THREE.Group,
) {
	addCeilingPlaneSlabs(ctx, ceilingGroup, {
		// The finished ceiling stops at the panel line, inside the structural
		// extent the hidden layers run to.
		halfWidth: getInteriorCeilingHalfWidth(ctx),
		thickness: INTERIOR_CEILING_PANEL_T,
		length: getInteriorCeilingLength(ctx),
		material: matCeilingPanel,
		centerYAt: (centerX) => getInteriorCeilingPanelCenterYAt(ctx, centerX, 0),
		name: (half) => `detail/ceiling-panel/${namePrefix}-${half}`,
	});
	buildCeilingTrim(ctx, ceilingGroup, namePrefix, frontCeilingGroup);
}

/**
 * Taklist / bomlist along the wall↔ceiling angle — the BOM row
 * `interior.ceiling-trim`, which had no geometry at all before issue 115.
 *
 * The four runs BUTT rather than cross (the gavel runs are shortened by the
 * side lists' thickness): same-slab trim members that overlap z-fight, the
 * lesson from the sockel/knutbräda junction. On a gable the gavel run
 * follows the roof fall as two sheared halves.
 */
function buildCeilingTrim(
	ctx: SquareBaseBuildContext,
	group: THREE.Group,
	namePrefix: string,
	frontGroup: THREE.Group,
) {
	const halfWidth = getInteriorCeilingHalfWidth(ctx);
	const halfLength = getInteriorCeilingLength(ctx) / 2;
	const prefix = `detail/ceiling-trim/${namePrefix}`;
	const isGable = ctx.roofConfig.kind === "gable";
	// Mono: the side runs follow (or, falling across the width, cross) the
	// fall; the end runs follow it only when the roof falls along X.
	const monoShear = getMonoCeilingShear(ctx);

	// The list is pushed 1 mm INTO the wall panel and 1 mm up into the ceiling
	// panel. Sitting flush would make its back and top faces coplanar with
	// the surfaces it covers; insetting the other way would open a hairline
	// gap along the very joint the list exists to hide.
	const embed = FACE_CLEARANCE_M;

	// Side runs (along Z, against the left/right walls).
	for (const side of [-1, 1] as const) {
		const x = side * (halfWidth - CEILING_TRIM_T / 2 + embed);
		const y =
			getInteriorCeilingUndersideYAt(ctx, x, 0) - CEILING_TRIM_H / 2 + embed;
		const name = `${prefix}-${side < 0 ? "left" : "right"}`;
		if (isGable) {
			// Level along its Z run, but — like the battens — its 22 mm width
			// still crosses the fall, so a flat box would open a ~3.5 mm slot
			// along the very joint the list is there to hide.
			addSlopedBox(
				CEILING_TRIM_T,
				CEILING_TRIM_H,
				2 * halfLength,
				matCeilingPanel,
				x,
				y,
				0,
				group,
				-side * ctx.pitchRad,
				"x",
			).name = name;
			continue;
		}
		addSlopedBox(
			CEILING_TRIM_T,
			CEILING_TRIM_H,
			2 * halfLength,
			matCeilingPanel,
			x,
			y,
			0,
			group,
			monoShear.pitchRad,
			monoShear.axis,
		).name = name;
	}

	// Gavel/end runs (along X), butting inside the side runs.
	const endHalfWidth = Math.max(0.05, halfWidth - CEILING_TRIM_T);
	for (const end of [-1, 1] as const) {
		const z = end * (halfLength - CEILING_TRIM_T / 2 + embed);
		const label = end < 0 ? "back" : "front";
		// Front run rides the front-wall visibility group, like front panels.
		const endGroup = end > 0 ? frontGroup : group;
		if (isGable) {
			// A gavel run crosses both roof halves, so it splits exactly like
			// the ceiling planes do — reuse that helper rather than keep a
			// fourth copy of the ±pitch sign convention.
			addCeilingPlaneSlabs(ctx, endGroup, {
				halfWidth: endHalfWidth,
				thickness: CEILING_TRIM_H,
				length: CEILING_TRIM_T,
				material: matCeilingPanel,
				z,
				centerYAt: (centerX) =>
					getInteriorCeilingUndersideYAt(ctx, centerX, z) -
					CEILING_TRIM_H / 2 +
					embed,
				name: (half) => `${prefix}-${label}-${half}`,
			});
			continue;
		}
		if (monoShear.axis === "x") {
			addSlopedBox(
				2 * endHalfWidth,
				CEILING_TRIM_H,
				CEILING_TRIM_T,
				matCeilingPanel,
				0,
				getInteriorCeilingUndersideYAt(ctx, 0, z) - CEILING_TRIM_H / 2 + embed,
				z,
				endGroup,
				monoShear.pitchRad,
				"x",
			).name = `${prefix}-${label}`;
			continue;
		}
		addBox(
			2 * endHalfWidth,
			CEILING_TRIM_H,
			CEILING_TRIM_T,
			matCeilingPanel,
			0,
			getInteriorCeilingUndersideYAt(ctx, 0, z) - CEILING_TRIM_H / 2 + embed,
			z,
			endGroup,
		).name = `${prefix}-${label}`;
	}
}

export function buildSaunaRoomCore(
	ctx: SquareBaseBuildContext,
	interiorGroup: THREE.Group,
	frontInteriorGroup: THREE.Group,
	options: SaunaCoreBuildOptions,
) {
	const heaterW = getHeaterWidth(options.heaterType);

	buildInteriorWallLayers(
		ctx,
		options.framingResults,
		interiorGroup,
		frontInteriorGroup,
		options.namePrefix,
		options.vaporBarrierZRange,
		options.panelDirection,
	);

	// Every lav comes from the resolved `InteriorLayout` — including the ones a
	// design that has never been edited gets, which are generated by the same
	// domain code the BOM reads. Nothing here decides where a bench is any more;
	// heater clipping and the wall-hung/posted distinction were both resolved
	// upstream by `createInteriorRenderPlan`, so the model cannot draw a bench
	// the material list did not buy.
	for (const bench of options.benches) {
		const benchMaterial = getBenchMaterial(bench.woodSpecies);
		addSlattedBench({
			group: interiorGroup,
			width: bench.widthM,
			depth: bench.depthM,
			height: BENCH_SLAT_THICKNESS_MM / 1000,
			legHeight: bench.legHeightM,
			slatCount: bench.slatCount,
			slatGapMm: bench.slatGapMm,
			material: benchMaterial,
			position: new THREE.Vector3(
				bench.centerXM,
				benchSeatCentreYM(bench.seatHeight, ctx.wallBase, ctx.H),
				bench.centerZM,
			),
			namePrefix: bench.nameSuffix,
			orientation: bench.orientation,
			support: bench.support,
			wallSide: bench.wallSide,
			skirt: bench.skirt,
		});
		if (bench.backrest) {
			for (const [kind, volumes] of [["board", bench.backrest.boards], ["spacer", bench.backrest.spacers]] as const) {
				volumes.forEach((v, i) => {
					const mesh = addBox((v.maxXMm-v.minXMm)/1000, (v.maxYMm-v.minYMm)/1000, (v.maxZMm-v.minZMm)/1000,
						benchMaterial, (v.minXMm+v.maxXMm)/2000, ctx.wallBase+(v.minYMm+v.maxYMm)/2000, (v.minZMm+v.maxZMm)/2000, interiorGroup);
					mesh.name = `detail/bench-backrest/${bench.nameSuffix}-${kind}-${i+1}`;
				});
			}
		}
	}

	buildHeaterGroup(
		options.heaterType,
		options.heater,
		interiorGroup,
		options.namePrefix,
		options.heaterRotationY ?? 0,
	);

	const frontShieldAlongX = options.frontShield.normal === "x";
	addBox(
		frontShieldAlongX ? 0.02 : heaterW + 0.3,
		1.1,
		frontShieldAlongX ? heaterW + 0.3 : 0.02,
		matShield,
		options.frontShield.position.x,
		options.frontShield.position.y,
		options.frontShield.position.z,
		options.frontShield.group,
	).name = `detail/shield/${options.namePrefix}-front`;
	// The side shield hangs on the wall the unit BACKS ONTO, so its thin axis is
	// that wall's normal. It used to be hard-coded as an X-normal plate, which is
	// right only while the aggregat stands on a long wall — an authored layout
	// may put it on a short one, and the plate then stranded on the right-hand
	// long wall metres away from the unit it was shielding.
	const sideShieldOnShortWall =
		options.sideShieldWall === "back" || options.sideShieldWall === "front";
	addBox(
		sideShieldOnShortWall ? heaterW + 0.3 : 0.02,
		1.1,
		sideShieldOnShortWall ? 0.02 : heaterW + 0.3,
		matShield,
		options.sideShield.x,
		options.sideShield.y,
		options.sideShield.z,
		interiorGroup,
	).name = `detail/shield/${options.namePrefix}-side`;

	if (heaterKindOf(options.heaterType) === "wood") {
		addBox(
			heaterW + 0.2,
			0.01,
			heaterW + 0.4,
			matChimney,
			options.heater.x,
			ctx.wallBase + 0.005,
			options.heater.z - 0.1,
			interiorGroup,
		).name = `detail/chimney/${options.namePrefix}-hearth-plate`;

		const roofHeightAtHeater = ctx.roofGeometry.getRoofHeightAt(
			options.heater.x,
			options.heater.z,
		);
		const roofCoveringThickness = getRoofCoveringThickness(ctx);
		const ceilingFlangeThickness = 0.004;
		// Plates keep the margins they had around the old Ø160 drawing, now
		// around the Ø220 insulated chimney the quote buys.
		const flashingSize = 2 * CHIMNEY_OUTER_RADIUS_M + 0.24;
		const flashingThickness = 0.006;
		const ceilingY =
			getInteriorCeilingUndersideYAt(ctx, options.heater.x, options.heater.z) -
			ceilingFlangeThickness / 2 -
			CHIMNEY_PLATE_SURFACE_GAP;
		const roofSurfaceY =
			ctx.wallBase +
			roofHeightAtHeater +
			ctx.rH +
			roofDeckingVerticalThicknessMm(ctx.pitchRad) / 1000 +
			ROOF_UNDERLAY_T +
			roofCoveringThickness;
		const roofY =
			roofSurfaceY + flashingThickness / 2 + CHIMNEY_PLATE_SURFACE_GAP;
		const plateSlope = getChimneyPlateSlope(ctx, options.heater.x);
		// Penetration hardware anchored to the ceiling/roof SURFACES — the
		// ceiling flange, the roof flashing plate and the storm collar — rides
		// the ceiling visibility subgroup: TAK off takes those surfaces down
		// and bare plates must not stay floating where they used to sit. The
		// pipe and its rain cap are continuous with the stove and stay.
		const penetrationGroup =
			getCeilingVisibilityGroup(interiorGroup) ?? interiorGroup;
		buildChimneyStack({
			group: interiorGroup,
			namePrefix: options.namePrefix,
			x: options.heater.x,
			z: options.heater.z,
			floorY: ctx.wallBase,
			stack: options.chimneyStack,
			stopAboveY: options.chimneyStack.throughHouse ? roofY : undefined,
		});
		const ceilingFlange = addSlopedBox(
			2 * CHIMNEY_OUTER_RADIUS_M + 0.2,
			ceilingFlangeThickness,
			2 * CHIMNEY_OUTER_RADIUS_M + 0.2,
			matRoofFlashing,
			options.heater.x,
			ceilingY,
			options.heater.z,
			penetrationGroup,
			plateSlope.pitchRad,
			plateSlope.axis,
		);
		ceilingFlange.name = `detail/chimney/${options.namePrefix}-ceiling-flange`;

		const flashingPlate = addSlopedBox(
			flashingSize,
			flashingThickness,
			flashingSize,
			matRoofFlashing,
			options.heater.x,
			roofY,
			options.heater.z,
			penetrationGroup,
			plateSlope.pitchRad,
			plateSlope.axis,
		);
		flashingPlate.name = `detail/chimney/${options.namePrefix}-roof-flashing`;

		const collarR = CHIMNEY_OUTER_RADIUS_M + 0.025;
		const collarH = 0.03;
		const collarGeom = createSlopedStormCollarGeometry(
			collarR,
			collarR + 0.01,
			collarH,
			24,
			plateSlope,
		);
		const stormCollar = new THREE.Mesh(collarGeom, matRoofFlashing);
		stormCollar.position.set(
			options.heater.x,
			roofY + flashingThickness / 2 + collarH / 2,
			options.heater.z,
		);
		stormCollar.castShadow = true;
		stormCollar.name = `detail/chimney/${options.namePrefix}-storm-collar`;
		penetrationGroup.add(stormCollar);
	}

	if (options.intakeVent) {
		addWallVent({
			group: options.intakeVent.group,
			position: options.intakeVent.position,
			facing: options.intakeVent.facing,
			kind: "supply",
			name: `${options.namePrefix}-intake`,
		});
	}

	if (options.exhaustVent) {
		addWallVent({
			group: options.exhaustVent.group,
			position: options.exhaustVent.position,
			facing: options.exhaustVent.facing,
			kind: "exhaust",
			name: `${options.namePrefix}-exhaust`,
		});
	}
}
